import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/apiRouting';
import { useAuth } from '../contexts/AuthContext';
import authenticatedFetch from '../utils/authenticatedFetch';
import { apiGet } from '../utils/apiGatewayMigration';

interface EquipmentDevice {
  deviceId: string;
  enterpriseId: string;
  storeId: string;
  storeName: string;
  manufacturer: string;
  equipmentType: string;
  modelNumber: string;
  serialNumber: string;
  status: 'active' | 'inactive' | 'maintenance' | 'decommissioned';
  connectionStatus: 'online' | 'offline' | 'error';
  healthScore: number;
  lastSeen: string | null;
  lastMetricsUpdate: string | null;
  location: {
    area: string;
    position: string;
  };
}

interface DashboardMetrics {
  totalDevices: number;
  onlineDevices: number;
  offlineDevices: number;
  avgHealthScore: number;
  criticalAlerts: number;
  devicesByStatus: {
    active: number;
    inactive: number;
    maintenance: number;
  };
}

interface EquipmentMonitoringDashboardProps {
  // No props needed - uses enterprise/store selectors
}

interface AirQualityReadings {
  co2: number | null;
  temperature: number | null;
  humidity: number | null;
  pm25: number | null;
  voc: number | null;
}

const EquipmentMonitoringDashboard: React.FC<EquipmentMonitoringDashboardProps> = () => {
  const navigate = useNavigate();
  const [devices, setDevices] = useState<EquipmentDevice[]>([]);
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedManufacturer, setSelectedManufacturer] = useState<string>('all');

  // Enterprise and Store selection state
  const [enterprises, setEnterprises] = useState<any[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const [selectedEnterprise, setSelectedEnterprise] = useState<string>('');
  const [selectedStore, setSelectedStore] = useState<string>('all');

  // SensusAir connect modal state
  const [showSensusairModal, setShowSensusairModal] = useState(false);
  const [sensusairAppId, setSensusairAppId] = useState('265');
  const [sensusairApiKey, setSensusairApiKey] = useState('');
  const [sensusairConnecting, setSensusairConnecting] = useState(false);
  const [sensusairError, setSensusairError] = useState<string | null>(null);
  const [sensusairSyncing, setSensusairSyncing] = useState(false);

  // Per-device live readings cache
  const [sensorReadings, setSensorReadings] = useState<Record<string, AirQualityReadings>>({});

  const { session } = useAuth();

  // Load enterprises on mount
  useEffect(() => {
    loadEnterprises();
  }, []);

  // Load stores when enterprise changes
  useEffect(() => {
    if (selectedEnterprise) {
      loadStores(selectedEnterprise);
    } else {
      setStores([]);
      setSelectedStore('all');
    }
  }, [selectedEnterprise]);

  // Load equipment data when enterprise or store changes
  useEffect(() => {
    if (selectedEnterprise) {
      fetchDashboardData();
    }
  }, [selectedEnterprise, selectedStore]);

  const loadEnterprises = async () => {
    try {
      const data = await apiGet('enterprises');
      const enterprisesList = Array.isArray(data) ? data : (data.data || []);
      setEnterprises(enterprisesList);

      // Auto-select first enterprise
      if (enterprisesList.length > 0) {
        setSelectedEnterprise(enterprisesList[0].enterpriseId);
      }
    } catch (error) {
      console.error('Error loading enterprises:', error);
      setError('Failed to load enterprises');
    }
  };

  const loadStores = async (enterpriseId: string) => {
    try {
      const data = await apiGet('enterpriseStores', enterpriseId);
      const storesList = Array.isArray(data) ? data : (data.data || []);
      setStores(storesList);
      setSelectedStore('all'); // Reset to all stores when enterprise changes
    } catch (error) {
      console.error('Error loading stores:', error);
      setStores([]);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (selectedStore && selectedStore !== 'all') {
        // Fetch dashboard data for specific store
        const dashboardResponse = await authenticatedFetch(
          API_ENDPOINTS.equipmentDashboard(selectedStore)
        );

        if (dashboardResponse.ok) {
          const data = await dashboardResponse.json();
          const dashboardData = data.success ? data.data : data;

          setMetrics(dashboardData.metrics);
          setDevices(dashboardData.devices || []);
        } else {
          throw new Error('Failed to fetch dashboard data');
        }
      } else if (selectedEnterprise) {
        // Fetch devices by enterprise (all stores)
        const devicesResponse = await authenticatedFetch(
          API_ENDPOINTS.equipmentDevices({ enterpriseId: selectedEnterprise })
        );

        if (devicesResponse.ok) {
          const data = await devicesResponse.json();
          const devicesData = data.success ? data.data : data;
          setDevices(devicesData.devices || []);

          // Calculate metrics from devices
          calculateMetrics(devicesData.devices || []);
        } else {
          throw new Error('Failed to fetch devices');
        }
      }
    } catch (err) {
      console.error('Error fetching equipment data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load equipment data');
    } finally {
      setLoading(false);
    }
  };

  // Sync SensusAir readings on dashboard load when sensusair devices exist
  useEffect(() => {
    if (selectedEnterprise && devices.some(d => d.manufacturer === 'sensusair')) {
      syncSensusairReadings();
    }
  }, [devices.length, selectedEnterprise]);

  const handleSensusairConnect = async () => {
    if (!sensusairAppId || !sensusairApiKey || !selectedEnterprise) return;

    setSensusairConnecting(true);
    setSensusairError(null);

    try {
      const response = await authenticatedFetch(API_ENDPOINTS.equipmentSensusairConnect(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appId: sensusairAppId,
          apiKey: sensusairApiKey,
          enterpriseId: selectedEnterprise,
          storeId: selectedStore !== 'all' ? selectedStore : selectedEnterprise
        })
      });

      if (response.ok) {
        const data = await response.json();
        const result = data.success ? data.data : data;
        setShowSensusairModal(false);
        setSensusairApiKey('');
        // Refresh dashboard to show new devices
        fetchDashboardData();
      } else {
        const errData = await response.json();
        setSensusairError(errData.message || 'Connection failed');
      }
    } catch (err) {
      setSensusairError(err instanceof Error ? err.message : 'Connection failed');
    } finally {
      setSensusairConnecting(false);
    }
  };

  const syncSensusairReadings = async () => {
    if (!selectedEnterprise) return;
    setSensusairSyncing(true);

    try {
      const response = await authenticatedFetch(
        API_ENDPOINTS.equipmentSensusairSync(selectedEnterprise)
      );

      if (response.ok) {
        // Refresh dashboard after sync
        fetchDashboardData();
      }
    } catch (err) {
      console.error('SensusAir sync error:', err);
    } finally {
      setSensusairSyncing(false);
    }
  };

  const calculateMetrics = (devicesList: EquipmentDevice[]) => {
    const totalDevices = devicesList.length;
    const onlineDevices = devicesList.filter(d => d.connectionStatus === 'online').length;
    const offlineDevices = devicesList.filter(d => d.connectionStatus === 'offline').length;
    const avgHealthScore = totalDevices > 0
      ? Math.round(devicesList.reduce((sum, d) => sum + d.healthScore, 0) / totalDevices)
      : 0;
    const criticalAlerts = devicesList.filter(d => d.healthScore < 50).length;

    const devicesByStatus = {
      active: devicesList.filter(d => d.status === 'active').length,
      inactive: devicesList.filter(d => d.status === 'inactive').length,
      maintenance: devicesList.filter(d => d.status === 'maintenance').length,
    };

    setMetrics({
      totalDevices,
      onlineDevices,
      offlineDevices,
      avgHealthScore,
      criticalAlerts,
      devicesByStatus,
    });
  };

  const getHealthColor = (score: number): string => {
    if (score >= 80) return '#28a745'; // Green
    if (score >= 60) return '#ffc107'; // Yellow
    if (score >= 40) return '#fd7e14'; // Orange
    return '#dc3545'; // Red
  };

  const getStatusBadge = (status: string): string => {
    const badges: Record<string, string> = {
      online: 'badge-success',
      offline: 'badge-danger',
      error: 'badge-warning',
      active: 'badge-primary',
      maintenance: 'badge-warning',
      inactive: 'badge-secondary',
    };
    return badges[status] || 'badge-secondary';
  };

  const filteredDevices = selectedManufacturer === 'all'
    ? devices
    : devices.filter(d => d.manufacturer === selectedManufacturer);

  const manufacturers = Array.from(new Set(devices.map(d => d.manufacturer)));

  if (loading) {
    return (
      <div className="equipment-monitoring-dashboard">
        <h2>Equipment Monitoring</h2>
        <p>Loading equipment data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="equipment-monitoring-dashboard">
        <h2>Equipment Monitoring</h2>
        <div className="alert alert-danger">{error}</div>
      </div>
    );
  }

  return (
    <div className="equipment-monitoring-dashboard">
      <h2>Equipment Monitoring Dashboard</h2>

      {/* Enterprise and Store Selectors */}
      <div style={{ marginBottom: '24px', display: 'flex', gap: '16px', alignItems: 'center' }}>
        <div style={{ flex: 1 }}>
          <label htmlFor="enterprise-select" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            Enterprise:
          </label>
          <select
            id="enterprise-select"
            value={selectedEnterprise}
            onChange={(e) => setSelectedEnterprise(e.target.value)}
            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
          >
            {enterprises.length === 0 && <option value="">Loading enterprises...</option>}
            {enterprises.map(enterprise => (
              <option key={enterprise.enterpriseId} value={enterprise.enterpriseId}>
                {enterprise.name}
              </option>
            ))}
          </select>
        </div>

        <div style={{ flex: 1 }}>
          <label htmlFor="store-select" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            Store:
          </label>
          <select
            id="store-select"
            value={selectedStore}
            onChange={(e) => setSelectedStore(e.target.value)}
            disabled={!selectedEnterprise || stores.length === 0}
            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
          >
            <option value="all">All Stores</option>
            {stores.map(store => (
              <option key={store.storeId} value={store.storeId}>
                {store.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Metrics Grid */}
      {metrics && (
        <div className="metrics-grid" style={{ marginBottom: '24px' }}>
          <div className="metric-card">
            <div className="metric-value">{metrics.totalDevices}</div>
            <div className="metric-label">Total Devices</div>
          </div>
          <div className="metric-card">
            <div className="metric-value">{metrics.onlineDevices}</div>
            <div className="metric-label">Online</div>
          </div>
          <div className="metric-card">
            <div className="metric-value">{metrics.offlineDevices}</div>
            <div className="metric-label">Offline</div>
          </div>
          <div className="metric-card">
            <div className="metric-value" style={{ color: getHealthColor(metrics.avgHealthScore) }}>
              {metrics.avgHealthScore}
            </div>
            <div className="metric-label">Avg Health Score</div>
          </div>
          <div className="metric-card">
            <div className="metric-value" style={{ color: metrics.criticalAlerts > 0 ? '#dc3545' : '#6c757d' }}>
              {metrics.criticalAlerts}
            </div>
            <div className="metric-label">Critical Alerts</div>
          </div>
        </div>
      )}

      {/* Manufacturer Filter & SensusAir Actions */}
      <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
        {manufacturers.length > 0 && (
          <div>
            <label htmlFor="manufacturer-filter" style={{ marginRight: '8px', fontWeight: 'bold' }}>
              Filter by Manufacturer:
            </label>
            <select
              id="manufacturer-filter"
              value={selectedManufacturer}
              onChange={(e) => setSelectedManufacturer(e.target.value)}
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
            >
              <option value="all">All Manufacturers</option>
              {manufacturers.map(mfr => (
                <option key={mfr} value={mfr}>
                  {mfr === 'sensusair' ? 'SensusAir' : mfr.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* SensusAir connect/sync buttons */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
          {devices.some(d => d.manufacturer === 'sensusair') ? (
            <button
              className="btn-modern-secondary"
              onClick={syncSensusairReadings}
              disabled={sensusairSyncing}
              style={{ fontSize: '13px', padding: '8px 16px' }}
            >
              {sensusairSyncing ? 'Syncing...' : 'Sync SensusAir'}
            </button>
          ) : (
            <button
              className="btn-modern-primary"
              onClick={() => setShowSensusairModal(true)}
              style={{ fontSize: '13px', padding: '8px 16px' }}
            >
              Connect SensusAir
            </button>
          )}
        </div>
      </div>

      {/* SensusAir Connect Modal */}
      {showSensusairModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#fff', borderRadius: '8px', padding: '32px',
            maxWidth: '440px', width: '90%', boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px' }}>Connect SensusAir</h3>
            <p style={{ color: '#6c757d', fontSize: '14px', marginBottom: '20px' }}>
              Enter your SensusAir API credentials to import air quality sensors.
            </p>

            {sensusairError && (
              <div style={{
                backgroundColor: '#f8d7da', color: '#721c24', padding: '10px 14px',
                borderRadius: '4px', marginBottom: '16px', fontSize: '14px'
              }}>
                {sensusairError}
              </div>
            )}

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '14px' }}>
                App ID
              </label>
              <input
                type="text"
                value={sensusairAppId}
                onChange={(e) => setSensusairAppId(e.target.value)}
                style={{
                  width: '100%', padding: '10px', borderRadius: '4px',
                  border: '1px solid #ccc', fontSize: '14px', boxSizing: 'border-box'
                }}
                placeholder="e.g. 265"
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '14px' }}>
                API Key
              </label>
              <input
                type="password"
                value={sensusairApiKey}
                onChange={(e) => setSensusairApiKey(e.target.value)}
                style={{
                  width: '100%', padding: '10px', borderRadius: '4px',
                  border: '1px solid #ccc', fontSize: '14px', boxSizing: 'border-box'
                }}
                placeholder="Enter your SensusAir API key"
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                className="btn-modern-secondary"
                onClick={() => { setShowSensusairModal(false); setSensusairError(null); }}
                style={{ padding: '10px 20px' }}
              >
                Cancel
              </button>
              <button
                className="btn-modern-primary"
                onClick={handleSensusairConnect}
                disabled={sensusairConnecting || !sensusairApiKey}
                style={{ padding: '10px 20px' }}
              >
                {sensusairConnecting ? 'Connecting...' : 'Connect'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Devices Grid */}
      <div className="dashboard-grid">
        {filteredDevices.length === 0 ? (
          <div className="dashboard-card">
            <h3>No Equipment Found</h3>
            <p>No equipment devices are registered for this location.</p>
          </div>
        ) : (
          filteredDevices.map(device => (
            <div
              key={device.deviceId}
              className="dashboard-card"
              onClick={() => navigate(`/equipment/${device.enterpriseId}/${device.deviceId}`)}
              style={{ cursor: 'pointer' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                <div>
                  <h4 style={{ margin: '0 0 4px 0' }}>
                    {device.manufacturer === 'sensusair' ? 'SensusAir' : device.manufacturer.toUpperCase()} {device.equipmentType === 'air-quality-sensor' ? 'Air Quality Sensor' : device.equipmentType}
                  </h4>
                  <p style={{ margin: '0', fontSize: '13px', color: '#6c757d' }}>
                    {device.manufacturer === 'sensusair'
                      ? `${(device as any).metadata?.areaName || device.location?.area || 'Unknown Area'}`
                      : `Model: ${device.modelNumber}`
                    }
                  </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div
                    className={`badge ${getStatusBadge(device.connectionStatus)}`}
                    style={{ display: 'inline-block', marginBottom: '4px' }}
                  >
                    {device.connectionStatus.toUpperCase()}
                  </div>
                </div>
              </div>

              {/* SensusAir air quality card */}
              {device.manufacturer === 'sensusair' ? (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px',
                    fontSize: '13px', marginBottom: '8px'
                  }}>
                    <div style={{ backgroundColor: '#f8f9fa', padding: '8px', borderRadius: '4px', textAlign: 'center' }}>
                      <div style={{ color: '#6c757d', marginBottom: '2px' }}>CO2</div>
                      <strong>{(device as any).metadata?.lastReading?.co2 ?? '--'} ppm</strong>
                    </div>
                    <div style={{ backgroundColor: '#f8f9fa', padding: '8px', borderRadius: '4px', textAlign: 'center' }}>
                      <div style={{ color: '#6c757d', marginBottom: '2px' }}>Temp</div>
                      <strong>{(device as any).metadata?.lastReading?.temperature != null
                        ? `${((device as any).metadata.lastReading.temperature * 9/5 + 32).toFixed(1)} F`
                        : '--'
                      }</strong>
                    </div>
                    <div style={{ backgroundColor: '#f8f9fa', padding: '8px', borderRadius: '4px', textAlign: 'center' }}>
                      <div style={{ color: '#6c757d', marginBottom: '2px' }}>Humidity</div>
                      <strong>{(device as any).metadata?.lastReading?.humidity ?? '--'}%</strong>
                    </div>
                    <div style={{ backgroundColor: '#f8f9fa', padding: '8px', borderRadius: '4px', textAlign: 'center' }}>
                      <div style={{ color: '#6c757d', marginBottom: '2px' }}>PM2.5</div>
                      <strong>{(device as any).metadata?.lastReading?.pm25 ?? '--'} ug/m3</strong>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontSize: '13px' }}>Air Quality Score:</span>
                    <strong style={{ color: getHealthColor(device.healthScore) }}>
                      {device.healthScore}/100
                    </strong>
                  </div>
                  <div style={{
                    height: '8px', backgroundColor: '#e9ecef',
                    borderRadius: '4px', overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${device.healthScore}%`, height: '100%',
                      backgroundColor: getHealthColor(device.healthScore),
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              ) : (
                /* Standard equipment health bar */
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontSize: '13px' }}>Health Score:</span>
                    <strong style={{ color: getHealthColor(device.healthScore) }}>
                      {device.healthScore}/100
                    </strong>
                  </div>
                  <div style={{
                    height: '8px', backgroundColor: '#e9ecef',
                    borderRadius: '4px', overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${device.healthScore}%`, height: '100%',
                      backgroundColor: getHealthColor(device.healthScore),
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              )}

              <div style={{ fontSize: '13px', color: '#6c757d' }}>
                <p style={{ margin: '4px 0' }}>
                  <strong>Location:</strong> {device.location?.area || 'Unknown'} - {device.location?.position || 'Unknown'}
                </p>
                {device.manufacturer !== 'sensusair' && (
                  <p style={{ margin: '4px 0' }}>
                    <strong>Serial:</strong> {device.serialNumber}
                  </p>
                )}
                <p style={{ margin: '4px 0' }}>
                  <strong>Status:</strong>{' '}
                  <span className={`badge ${getStatusBadge(device.status)}`}>
                    {device.status.toUpperCase()}
                  </span>
                </p>
                {device.lastSeen && (
                  <p style={{ margin: '4px 0' }}>
                    <strong>Last Seen:</strong> {new Date(device.lastSeen).toLocaleString()}
                  </p>
                )}
              </div>

              <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                <button
                  className="btn-modern-secondary"
                  style={{ flex: 1, fontSize: '13px', padding: '6px 12px' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/equipment/${device.enterpriseId}/${device.deviceId}`);
                  }}
                >
                  View Details
                </button>
                <button
                  className="btn-modern-accent"
                  style={{ flex: 1, fontSize: '13px', padding: '6px 12px' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/equipment/${device.enterpriseId}/${device.deviceId}`);
                  }}
                >
                  Manage
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default EquipmentMonitoringDashboard;
